import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Switch,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Autocomplete,
  CircularProgress,
  Tooltip,
  Stack,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Category as CategoryIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { webCategoryService } from '../../services/webCategoryService';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';
import DeleteConfirmDialog from '../../components/Common/DeleteConfirmDialog';
import useResponsive from '../../hooks/useResponsive';

const BulkImportWebCategoryList = () => {
  const isMobile = useResponsive('down', 'sm');
  const menuDispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    menuDispatch({ type: 'menuSelect', payload: 'web-subcategory' });
  }, [menuDispatch]);

  // ── Table state ───────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // ── Filters ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [parentCategories, setParentCategories] = useState([]);
  const [loadingParentCategories, setLoadingParentCategories] = useState(false);

  // ── Delete dialog ─────────────────────────────────────────────────────────
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ── Snackbar ──────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Load parent categories for filter dropdown ────────────────────────────
  useEffect(() => {
    setLoadingParentCategories(true);
    webCategoryService
      .getParentCategoriesForDropdown({ limit: 200, isActive: true })
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setParentCategories(list);
      })
      .catch(() => {})
      .finally(() => setLoadingParentCategories(false));
  }, []);

  // ── Fetch table data ──────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 500,
        ...(filterActive !== null && { isActive: filterActive }),
        ...(filterCategoryId !== null && { categoryId: filterCategoryId })
      };
      const response = await webCategoryService.getLocationWiseServices(params);
      if (response.success) {
        let data = response.data || [];
        let flattened = data.map(item => {
          const merged = {
            ...item,
            ...(item.locationData || {})
          };
          return {
            ...merged,
            name: merged.name || merged.locationName || '-',
            slug: merged.slug || merged.locationUrl || '-',
            location: merged.location || merged.locationName || '-',
            heading: merged.heading || '-',
            subheading: merged.subheading || merged.subHeading || '-'
          };
        });
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          flattened = flattened.filter(
            (item) =>
              (item.name || '').toLowerCase().includes(q) ||
              (item.slug || '').toLowerCase().includes(q) ||
              (item.location || '').toLowerCase().includes(q)
          );
        }
        setTotalRows(flattened.length);
        const start = paginationModel.page * paginationModel.pageSize;
        setRows(flattened.slice(start, start + paginationModel.pageSize));
      }
    } catch {
      showSnackbar('Failed to load location-wise services', 'error');
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchQuery, filterActive, filterCategoryId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Toggle status ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (row) => {
    try {
      const res = await webCategoryService.updateCategory(row._id, {
        isActive: !row.isActive
      });
      if (res.success) {
        fetchData();
        showSnackbar('Status updated');
      }
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleToggleDirectLocation = async (row) => {
    try {
      const res = await webCategoryService.updateCategory(row._id, {
        directLocationPage: !row.directLocationPage
      });
      if (res.success) {
        fetchData();
        showSnackbar('Direct Location Page updated');
      }
    } catch {
      showSnackbar('Failed to update', 'error');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setOpenDelete(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const res = await webCategoryService.deleteCategory(selectedRow._id);
      if (res.success) {
        setOpenDelete(false);
        fetchData();
        showSnackbar('Entry deleted successfully');
      }
    } catch {
      showSnackbar('Failed to delete entry', 'error');
    }
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'slug',
      headerName: 'Location URL',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value || '-'} 
          size="small" 
          variant="outlined" 
          sx={{ fontSize: 11, maxWidth: '100%' }} 
        />
      )
    },
    {
      field: 'heading',
      headerName: 'Heading',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap color="text.secondary">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'subheading',
      headerName: 'Sub Heading',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap color="text.secondary">
          {params.value || params.row.subHeading || '-'}
        </Typography>
      )
    },
    
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={!!params.value}
          onChange={() => handleToggleStatus(params.row)}
          color="primary"
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/admin/web-category-import/edit/${params.row._id}`)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ── Active filter options ─────────────────────────────────────────────────
  const activeOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <PageHeaderLayout headerTitle="Bulk Import Web Categories">
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/web-category-import')}
            size="small"
          >
            Add
          </Button>
        </Stack>
      </PageHeaderLayout>

      {/* Summary chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Chip
          icon={<CategoryIcon />}
          label={`${totalRows} Total Entries`}
          color="primary"
          variant="outlined"
          size="small"
        />
        <Chip
          icon={<ActiveIcon />}
          label={`${rows.filter((r) => r.isActive).length} Active`}
          color="success"
          variant="outlined"
          size="small"
        />
       
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Filters */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default'
          }}
        >
          <FilterIcon fontSize="small" color="action" />
          <TextField
            size="small"
            placeholder="Search name, slug, location..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1, minWidth: 220 }}
          />

          <Autocomplete
            options={parentCategories}
            loading={loadingParentCategories}
            value={parentCategories.find(
              (c) => (c?.id || c?._id) === filterCategoryId
            ) || null}
            onChange={(_, val) => {
              setFilterCategoryId(val ? val?.id || val?._id : null);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            getOptionLabel={(opt) =>
              opt?.title || opt?.name || opt?.categoryName || String(opt?.id || '')
            }
            isOptionEqualToValue={(opt, val) =>
              (opt?.id || opt?._id) === (val?.id || val?._id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Parent Category"
                placeholder="All categories"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingParentCategories ? (
                        <CircularProgress size={14} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            sx={{ minWidth: 220 }}
          />

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {activeOptions.map((opt) => (
              <Chip
                key={String(opt.value)}
                label={opt.label}
                size="small"
                color={filterActive === opt.value ? 'primary' : 'default'}
                variant={filterActive === opt.value ? 'filled' : 'outlined'}
                onClick={() => {
                  setFilterActive(opt.value);
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* DataGrid */}
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id || row.id || row.slug}
          loading={loading}
          rowCount={totalRows}
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'grey.50',
              fontWeight: 700
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: 'action.hover'
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'center',
              py: 1
            }
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 6
                }}
              >
                <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body1" color="text.secondary">
                  No web category entries found
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/admin/web-category-import')}
                  sx={{ mt: 1 }}
                >
                  Add
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Web Category Entry"
        content={`Are you sure you want to delete "${selectedRow?.name}"? This action cannot be undone.`}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkImportWebCategoryList;
